import React from "react";
import { Link } from "@inertiajs/react";
import { Box, Button, Grid, Group, Stack } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { Heading2 } from "./UI/Typography";
import { PostCard } from "./PostCard";
import type { PillarSlug } from "../data/posts";
import { getCrossPillarPosts, getPostsByPillar } from "../data/posts";

interface LatestPostsStripProps {
  pillar: PillarSlug;
  /** Max primary posts to show. Default 3. */
  max?: number;
  /** Section heading — defaults to "Latest posts". */
  heading?: string;
  /**
   * When true, skip the primary "Latest posts" block and only render the
   * "Related from other pillars" block. Useful on /think where the primary
   * posts already fill the page.
   */
  crossPillarOnly?: boolean;
}

/**
 * Drop-in strip for pillar landing pages. Shows up to `max` recent posts
 * from this pillar as PostCards + links to the format-specific indexes.
 * Also surfaces up to 2 cross-pillar posts (posts whose canonical home is
 * a different pillar but which name this pillar in crossPillars).
 * Renders nothing if there's no content in either bucket.
 */
export const LatestPostsStrip: React.FC<LatestPostsStripProps> = ({
  pillar,
  max = 3,
  heading = "Latest posts",
  crossPillarOnly = false,
}) => {
  const posts = crossPillarOnly ? [] : getPostsByPillar(pillar).slice(0, max);
  const crossPosts = getCrossPillarPosts(pillar).slice(0, 2);

  if (posts.length === 0 && crossPosts.length === 0) {
    return null;
  }

  return (
    <Stack gap="xxl">
      {posts.length > 0 && (
        <Box>
          <Group
            justify="space-between"
            align="center"
            mb="lg"
            wrap="wrap"
            gap="md"
          >
            <Heading2 variant="shadow">{heading}</Heading2>
            <Group gap="xs" wrap="wrap">
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                component={Link}
                href={`/${pillar}/articles`}
              >
                Articles →
              </Button>
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                component={Link}
                href={`/${pillar}/videos`}
              >
                Videos →
              </Button>
              <Button
                variant="subtle"
                color="gray"
                size="xs"
                component={Link}
                href={`/${pillar}/podcasts`}
              >
                Podcasts →
              </Button>
            </Group>
          </Group>
          <Grid>
            {posts.map((post) => (
              <Grid.Col key={post.slug} span={{ base: 12, sm: 6, lg: 4 }}>
                <PostCard post={post} />
              </Grid.Col>
            ))}
          </Grid>
        </Box>
      )}

      {crossPosts.length > 0 && (
        <Box>
          <Group justify="space-between" align="center" mb="lg">
            <Heading2 variant="shadow">Related from other pillars</Heading2>
            <IconArrowRight size={20} style={{ opacity: 0.4 }} />
          </Group>
          <Grid>
            {crossPosts.map((post) => (
              <Grid.Col key={post.slug} span={{ base: 12, sm: 6, lg: 4 }}>
                <PostCard post={post} fromPillarBadge />
              </Grid.Col>
            ))}
          </Grid>
        </Box>
      )}
    </Stack>
  );
};
